import React, { useState, useEffect } from 'react';
import { Search, FileText, Eye, Download, Loader, AlertCircle, X } from 'lucide-react';
import styles from './ConsentManager.module.css';
import { getConsents, getConsentsFiles, updateConsent } from '../../Config/api';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';

const ConsentManager = ({ userRole = 'CRO', currentPIName = 'Mahesh' }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [submissions, setSubmissions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userId, setUserId] = useState(null);

    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showReasonModal, setShowReasonModal] = useState(false);
    const [selectedConsentId, setSelectedConsentId] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        fetchSubmissions();
    }, []);

    const fetchSubmissions = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const userDetails = JSON.parse(localStorage.getItem('user')) || {};
            const role = userDetails.role || userRole;
            const currentUserId = userDetails.userId;

            setUserId(currentUserId);

            console.log('Fetching consents for role:', role);
            console.log('User ID:', currentUserId);

            const requestData = {
                userId: currentUserId,
                role: role
            };

            const response = await getConsents(requestData);

            console.log('Full Consents response:', JSON.stringify(response, null, 2));

            let consentsList = [];

            if (Array.isArray(response.data)) {
                consentsList = response.data;
            } else if (response.data && typeof response.data === 'object') {
                consentsList = [response.data];
            } else if (Array.isArray(response)) {
                consentsList = response;
            } else {
                console.error('Unexpected response structure:', response);
                consentsList = [];
            }

            console.log('Consents list to process:', consentsList);

            const mappedSubmissions = consentsList.map(consent => {
                console.log('Processing consent:', consent);
                console.log('Uploaded docs:', consent.uploaded_docs);

                const documents = [];

                if (consent.uploaded_docs) {
                    console.log('Uploaded docs type:', typeof consent.uploaded_docs);
                    console.log('Uploaded docs keys:', Object.keys(consent.uploaded_docs));

                    Object.entries(consent.uploaded_docs).forEach(([docType, docInfo]) => {
                        console.log(`Processing doc type: ${docType}`, docInfo);

                        if (docInfo && docInfo.filename && docInfo.originalName) {
                            documents.push({
                                name: docInfo.originalName,
                                filename: docInfo.filename,
                                type: docType
                            });
                            console.log('Added document:', docInfo.originalName);
                        }
                    });
                }

                console.log('Total documents found:', documents.length);

                let status = 'PENDING';
                const consentStatus = consent.consent_status?.toLowerCase();

                if (consentStatus === 'a' || consentStatus === 'approved') {
                    status = 'APPROVED';
                } else if (consentStatus === 'r' || consentStatus === 'rejected') {
                    status = 'REJECTED';
                } else if (consentStatus === 'p' || consentStatus === 'pending') {
                    status = 'PENDING';
                }

                const mappedConsent = {
                    id: consent.id,
                    piName: consent.full_name || 'N/A',
                    protocolNumber: consent.study_number || 'N/A',
                    studyName: consent.study_title || 'N/A',
                    siteDetails: {
                        code: consent.site_code || 'N/A',
                        name: consent.site_name || 'N/A'
                    },
                    maxSubjects: consent.max_subjects || 0,
                    submittedOn: consent.created_at || new Date().toISOString(),
                    status: status,
                    documents: documents,
                    reason: consent.reason || null
                };

                console.log('Mapped consent:', mappedConsent);
                return mappedConsent;
            });

            console.log('Final mapped submissions:', mappedSubmissions);
            setSubmissions(mappedSubmissions);

        } catch (error) {
            console.error('Error fetching submissions:', error);
            setError(error.response?.data?.message || 'Failed to load submissions. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };
    const getFilteredSubmissions = () => {
        let filtered = [...submissions];

        console.log('All submissions before filter:', submissions);
        console.log('User role:', userRole);

        if (userRole === 'PI') {
            const userDetails = JSON.parse(localStorage.getItem('user')) || {};
            const currentUserName = userDetails.fullName || currentPIName;

            console.log('Filtering for PI:', currentUserName);

            filtered = submissions.filter(sub => {
                console.log('Checking submission PI name:', sub.piName, 'against:', currentUserName);
                return sub.piName === currentUserName;
            });

            console.log('Filtered submissions for PI:', filtered);
        }

        if (searchQuery) {
            filtered = filtered.filter(sub =>
                sub.piName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                sub.protocolNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
                sub.siteDetails.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
                sub.siteDetails.name.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        console.log('Final filtered submissions:', filtered);
        return filtered;
    };

    const handleApprove = async (id) => {
        try {
            setIsSubmitting(true);

            const data = {
                consentId: id,
                status: 'a',
                reason: ''
            };

            const response = await updateConsent(data);

            console.log('Approve response:', response);

            if (response.success || response.data?.success || response.data?.message === 'Consent status updated' || response.message === 'Consent status updated') {
                alert('Consent approved successfully!');
                setTimeout(async () => {
                    await fetchSubmissions();
                }, 500);
            } else {
                alert('Consent approved successfully!');
                setTimeout(async () => {
                    await fetchSubmissions();
                }, 500);
            }

        } catch (error) {
            console.error('Error approving consent:', error);

            if (error.message === 'Consent status updated' || error.response?.data?.message === 'Consent status updated') {
                alert('Consent approved successfully!');
                setTimeout(async () => {
                    await fetchSubmissions();
                }, 500);
            } else {
                alert(error.response?.data?.message || error.message || 'Failed to approve consent. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRejectClick = (id) => {
        setSelectedConsentId(id);
        setShowRejectModal(true);
    };

    const handleRejectConfirm = () => {
        setShowRejectModal(false);
        setShowReasonModal(true);
    };

    const handleRejectCancel = () => {
        setShowRejectModal(false);
        setSelectedConsentId(null);
    };

    const handleReasonSubmit = async () => {
        if (!rejectReason.trim()) {
            alert('Please provide a reason for rejection');
            return;
        }

        try {
            setIsSubmitting(true);

            const data = {
                consentId: selectedConsentId,
                userId: userId,
                status: 'r',
                reason: rejectReason
            };

            const response = await updateConsent(data);

            console.log('Reject response:', response);

            if (response.success || response.data?.success || response.data?.message === 'Consent status updated' || response.message === 'Consent status updated') {
                alert('Consent rejected successfully!');
                setShowReasonModal(false);
                setRejectReason('');
                setSelectedConsentId(null);
                setTimeout(async () => {
                    await fetchSubmissions();
                }, 500);
            } else {
                alert('Consent rejected successfully!');
                setShowReasonModal(false);
                setRejectReason('');
                setSelectedConsentId(null);
                setTimeout(async () => {
                    await fetchSubmissions();
                }, 500);
            }

        } catch (error) {
            console.error('Error rejecting consent:', error);

            if (error.message === 'Consent status updated' || error.response?.data?.message === 'Consent status updated') {
                alert('Consent rejected successfully!');
                setShowReasonModal(false);
                setRejectReason('');
                setSelectedConsentId(null);
                setTimeout(async () => {
                    await fetchSubmissions();
                }, 500);
            } else {
                alert(error.response?.data?.message || error.message || 'Failed to reject consent. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReasonCancel = () => {
        setShowReasonModal(false);
        setRejectReason('');
        setSelectedConsentId(null);
    };

    const handleViewDocument = async (filename, documentName) => {
        setShowPreviewModal(true);
        setPreviewLoading(true);
        setPreviewError(null);
        setPreviewFile(null);

        try {
            console.log('Fetching file:', filename);

            const response = await getConsentsFiles({ files: [filename] });
            // const filesData = response.data.files;

            console.log('File response:', response);


            let fileUrl = null;
            let fileType = null;

            if (response.files[0].filename) {
                fileUrl = response.files[0].url;
                fileType = response.files[0].type || response.files[0].mimeType;
            } else if (response.files[0]?.file) {
                fileUrl = response.files[0].file;
            } else if (response.url) {
                fileUrl = response.url;
            } else if (typeof response.data === 'string') {
                fileUrl = response.data;
            }
            console.log('Determined file URL:', fileUrl);
            if (fileUrl && fileUrl.startsWith('data:')) {

                setPreviewFile({
                    url: fileUrl,
                    name: documentName,
                    type: fileType || getFileTypeFromName(documentName)
                });
            } else if (fileUrl) {
                setPreviewFile({
                    url: fileUrl,
                    name: documentName,
                    type: fileType || getFileTypeFromName(documentName)
                });
            } else {
                throw new Error('File URL not found in response');
            }


        } catch (error) {
            console.error('Error fetching file:', error);
            setPreviewError(error.response?.data?.message || 'Failed to load file preview. Please try again.');
        } finally {
            setPreviewLoading(false);
        }
    };

    const getFileTypeFromName = (filename) => {
        const extension = filename.split('.').pop().toLowerCase();
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
        const pdfExtensions = ['pdf'];

        if (imageExtensions.includes(extension)) {
            return 'image';
        } else if (pdfExtensions.includes(extension)) {
            return 'pdf';
        }
        return 'unknown';
    };

    const handleClosePreview = () => {
        setShowPreviewModal(false);
        setPreviewFile(null);
        setPreviewError(null);
    };

    const handleDownloadDocument = (filename, documentName) => {
        if (filename) {
            const fileUrl = `${process.env.REACT_APP_API_BASE_URL || ''}/files/${filename}`;
            const link = document.createElement('a');
            link.href = fileUrl;
            link.download = documentName;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            alert('Document not available');
        }
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'PENDING':
                return styles.statusPending;
            case 'APPROVED':
                return styles.statusApproved;
            case 'REJECTED':
                return styles.statusRejected;
            default:
                return styles.statusPending;
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    if (isLoading) {
        return (
            <div className={styles.container}>
                <div className={styles.loadingWrapper}>
                    <Loader className={`${styles.loadingIcon} ${styles.spinning}`} />
                    <p className={styles.loadingText}>Loading submissions...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.container}>
                <div className={styles.errorWrapper}>
                    <AlertCircle className={styles.errorIconLarge} />
                    <h2 className={styles.errorTitle}>Failed to Load Submissions</h2>
                    <p className={styles.errorMessage}>{error}</p>
                    <button onClick={fetchSubmissions} className={styles.retryButton}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    const filteredSubmissions = getFilteredSubmissions();

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div>
                    <h1 className={styles.title}>Consent Manager</h1>
                    <p className={styles.subtitle}>
                        Review and manage principal investigator consent submissions
                    </p>
                </div>
            </div>

            <div className={styles.searchWrapper}>
                <div className={styles.searchContainer}>
                    <Search className={styles.searchIcon} />
                    <input
                        type="text"
                        placeholder="Search by PI name, study number, site code, or site name..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={styles.searchInput}
                    />
                </div>
            </div>

            <div className={styles.submissionsContainer}>
                {filteredSubmissions.length === 0 ? (
                    <div className={styles.emptyState}>
                        <FileText className={styles.emptyIcon} size={48} />
                        <p>No submissions found</p>
                    </div>
                ) : (
                    filteredSubmissions.map((submission) => (
                        <div key={submission.id} className={styles.card}>
                            <div className={styles.cardHeader}>
                                <div className={styles.cardHeaderLeft}>
                                    <h2 className={styles.piName}>{submission.piName}</h2>
                                    <span className={`${styles.statusBadge} ${getStatusBadgeClass(submission.status)}`}>
                                        {submission.status}
                                    </span>
                                </div>

                                <div className={styles.cardHeaderRight}>
                                    {userRole !== 'PI' && submission.status === 'PENDING' && (
                                        <div className={styles.actionButtons}>
                                            <button
                                                onClick={() => handleRejectClick(submission.id)}
                                                className={styles.rejectButton}
                                                disabled={isSubmitting}
                                            >
                                                Reject
                                            </button>
                                            <button
                                                onClick={() => handleApprove(submission.id)}
                                                className={styles.approveButton}
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? 'Processing...' : 'Approve'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className={styles.cardDetails}>
                                <div className={styles.detailItem}>
                                    <span className={styles.detailLabel}>Study Details</span>
                                    <span className={styles.detailValue}>{submission.protocolNumber} <br />{submission.studyName} </span>
                                </div>

                                <div className={styles.detailItem}>
                                    <span className={styles.detailLabel}>Site Details</span>
                                    <span className={styles.detailValue}>
                                        {submission.siteDetails.code}
                                        <br />
                                        {submission.siteDetails.name}
                                    </span>
                                </div>

                                <div className={styles.detailItem}>
                                    <span className={styles.detailLabel}>Max. Subjects</span>
                                    <span className={styles.detailValue}>{submission.maxSubjects}</span>
                                </div>

                                <div className={styles.detailItem}>
                                    <span className={styles.detailLabel}>Submitted on</span>
                                    <span className={styles.detailValue}>{formatDate(submission.submittedOn)}</span>
                                </div>
                            </div>

                            {submission.status === 'REJECTED' && submission.reason && (
                                <div className={styles.rejectionReason}>
                                    <span className={styles.detailLabel}>Rejection Reason:</span>
                                    <span className={styles.rejectionText}>{submission.reason}</span>
                                </div>
                            )}

                            {/* Submitted Documents */}
                            {submission.documents && submission.documents.length > 0 ? (
                                <div className={styles.documentsSection}>
                                    <h3 className={styles.sectionTitle}>Submitted Documents ({submission.documents.length})</h3>
                                    <div className={styles.documentsList}>
                                        {submission.documents.map((doc, index) => (
                                            <div key={index} className={styles.documentItem}>
                                                <FileText className={styles.documentIcon} />
                                                <span className={styles.documentName}>
                                                    {doc.name}
                                                </span>
                                                <div className={styles.documentActions}>
                                                    <button
                                                        className={styles.iconButton}
                                                        title="Preview"
                                                        onClick={() => handleViewDocument(doc.filename, doc.name)}
                                                    >
                                                        <Eye className={styles.iconSmall} />
                                                    </button>
                                                    <button
                                                        className={styles.iconButton}
                                                        title="Download"
                                                        onClick={() => handleDownloadDocument(doc.filename, doc.name)}
                                                    >
                                                        <Download className={styles.iconSmall} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.noDocuments}>
                                    <p>No documents submitted</p>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            <ConfirmationModal
                show={showRejectModal}
                onConfirm={handleRejectConfirm}
                onCancel={handleRejectCancel}
                title="Confirm Rejection"
                message="Are you sure you want to reject this consent? You will be asked to provide a reason."
                confirmText="Continue"
                cancelText="Cancel"
                type="warning"
            />

            {showReasonModal && (
                <div className={styles.modalOverlay} onClick={handleReasonCancel}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>Reason for Rejection</h3>
                            <button
                                className={styles.modalCloseButton}
                                onClick={handleReasonCancel}
                                aria-label="Close"
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <textarea
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                placeholder="Enter reason for rejection..."
                                className={styles.rejectTextarea}
                                rows={5}
                                autoFocus
                            />
                        </div>
                        <div className={styles.modalFooter}>
                            <button
                                onClick={handleReasonCancel}
                                className={styles.cancelButton}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleReasonSubmit}
                                className={styles.submitButton}
                                disabled={isSubmitting || !rejectReason.trim()}
                            >
                                {isSubmitting ? 'Submitting...' : 'Submit'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showPreviewModal && (
                <div className={styles.modalOverlay} onClick={handleClosePreview}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>
                                {previewFile?.name || 'Document Preview'}
                            </h3>
                            <button
                                className={styles.modalCloseButton}
                                onClick={handleClosePreview}
                                aria-label="Close"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className={styles.modalBody}>
                            {previewLoading && (
                                <div className={styles.previewLoading}>
                                    <Loader className={`${styles.loadingIcon} ${styles.spinning}`} />
                                    <p>Loading preview...</p>
                                </div>
                            )}

                            {previewError && (
                                <div className={styles.previewError}>
                                    <AlertCircle className={styles.errorIcon} />
                                    <p>{previewError}</p>
                                </div>
                            )}
                            {previewFile && !previewLoading && !previewError && (
                                <div className={styles.previewContainer}>
                                    {previewFile.url ||
                                        previewFile.name.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i) ? (
                                        <img
                                            src={previewFile.url}
                                            alt={previewFile.name}
                                            className={styles.previewImage}
                                        />
                                    ) : previewFile.type === 'pdf' || previewFile.name.endsWith('.pdf') ? (
                                        <iframe
                                            src={previewFile.url}
                                            className={styles.previewIframe}
                                            title={previewFile.name}
                                        />
                                    ) : (
                                        <div className={styles.previewUnsupported}>
                                            <FileText size={48} />
                                            <p>Preview not available for this file type</p>
                                            <button
                                                className={styles.downloadButton}
                                                onClick={() => handleDownloadDocument(previewFile.filename, previewFile.name)}
                                            >
                                                Download File
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConsentManager;

